'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React, { useCallback, useMemo, useState } from 'react';
import { IconChevronLeft } from '@tabler/icons-react';
import { TelegramTransactionPartByAmount, TokenResponse } from '@/generated';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TextInput } from '@/components/ui/text-input';
import { TooltipedElem } from '../../../TooltipedElem';
import { RadioGroup, RadioGroupOption } from '../../../Inputs/RadioGroup';
import { Link } from '../../../PrivateLink/ServerLink';
import { runtimeConfig } from '@/config/runtime-config';
import { Spinner } from '@/components/ui/spinner';

const isTGMiniApp = runtimeConfig.domain.toLowerCase().startsWith('tg');

type Props = {
  onClose: () => void;
  name: string;
  amountToPay: number;
  tokens: TokenResponse[];
  parts: TelegramTransactionPartByAmount[];
  onBack: () => void;
  onGoPay: (partsAmount: number, payerName: string, token?: string) => void;
  purchasing?: boolean;
  showSubscriptionTerms?: boolean;
  isFree?: boolean;
};

const USE_NEW_CARD_ID = 'USE_NEW_CARD';

const convertPartToText = (val: TelegramTransactionPartByAmount) => {
  return `${val.parts} x ${val.fiatAmount} ${val.currencySign}`;
};

export const CardComPayment: React.FC<Props> = ({
  amountToPay,
  tokens,
  onClose,
  onGoPay,
  onBack,
  parts,
  name,
  purchasing,
  showSubscriptionTerms,
  isFree,
}) => {
  const t = useTranslations('monetization');

  const [nameText, setNameText] = useState('');
  const [selectedPart, setSelectedPart] = useState(parts.find((val) => val.parts === 1));
  const [selectedCardId, setSelectedCardId] = useState(USE_NEW_CARD_ID);

  const updatePart = useCallback(
    (newSelectedParts: string) => {
      setSelectedPart(parts.find((val) => val.parts === Number(newSelectedParts)));
    },
    [parts],
  );

  const onChangeSelected = useCallback((val: string) => {
    if (val) {
      setSelectedCardId(val);
      return;
    }
  }, []);

  const onPay = useCallback(() => {
    onGoPay(selectedPart?.parts || 1, nameText, selectedCardId === USE_NEW_CARD_ID ? undefined : selectedCardId);
  }, [selectedCardId, onGoPay, nameText, selectedPart]);

  const tokensOptions = useMemo(() => {
    const res: RadioGroupOption<string>[] = [
      ...tokens.map((val) => ({
        value: val.id,
        label: t('card_com_use_card', { lastDigits: val.last4CardDigits ?? '' }),
      })),
      { value: USE_NEW_CARD_ID, label: t('card_com_use_new_card') },
    ];

    if (res[0].value !== USE_NEW_CARD_ID) {
      res[0].tooltip = (
        <div
          className='shrink-0 flex'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <TooltipedElem
            showOnHover
            duration={6000}
            toolTipContainerClassName='max-w-[280px]'
            tooltipText={t('card_com_card_storage_hint')}
          >
            <div className='size-[20px] min-w-[20px]'>
              <Image src='/assets/icons/question-icon.svg' width={20} height={20} alt='' />
            </div>
          </TooltipedElem>
        </div>
      );
    }

    return res;
  }, [t, tokens]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-[375px]'>
        <Button
          variant='ghost'
          size='icon'
          onClick={onBack}
          className='absolute top-4 start-4 z-10 h-8 w-8 rounded-full hover:bg-transparent'
        >
          <IconChevronLeft className='text-text-primary' />
        </Button>
        <div className='flex flex-col'>
          <DialogHeader className='items-center text-center'>
            <DialogTitle>{name}</DialogTitle>
          </DialogHeader>

          <div className=' py-[12px] flex justify-between items-center gap-[8px] mt-[24px]'>
            <span className='text-[16px] leading-[20px]'>{t('to_pay')}</span>

            <div className='ms-[8px] text-[22px] font-semibold leading-[20px]'>
              {isFree ? t('card_com_free') : `${amountToPay} ₪`}
            </div>
          </div>

          {parts.length > 1 && (
            <div className=' py-[12px] flex justify-between gap-[8px] mt-[24px] items-center'>
              <span className='text-[16px] leading-[20px]'>{t('card_com_payments_count_label')}</span>

              <div className='min-w-[40%] max-w-[50%]'>
                <Select value={String(selectedPart?.parts || 1)} onValueChange={updatePart}>
                  <SelectTrigger className='w-full h-[44px]! border-primary-stroke-day bg-white text-[16px]'>
                    <SelectValue placeholder={t('card_com_payments_count_placeholder')} />
                  </SelectTrigger>
                  <SelectContent position='popper'>
                    {parts.map((val) => (
                      <SelectItem key={val.parts} value={String(val.parts)}>
                        {convertPartToText(val)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className='mt-[24px] text-[14px] font-normal'>{t('card_com_receipt_name_label')}</div>

          <div className='relative mt-[4px]'>
            <TextInput
              label={t('card_com_receipt_name_placeholder')}
              value={nameText}
              onChange={(e) => {
                setNameText(e.target.value);
              }}
            />
            <div className='absolute top-[10px] end-[10px]'>
              <TooltipedElem
                showOnHover
                duration={6000}
                toolTipContainerClassName='max-w-[280px]'
                tooltipText={t('card_com_receipt_name_hint')}
              >
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-auto w-[20px] min-w-[20px] p-0 mt-[4px] hover:bg-transparent'
                >
                  <Image src='/assets/icons/question-icon.svg' width={20} height={20} alt='' />
                </Button>
              </TooltipedElem>
            </div>
          </div>

          {tokensOptions.length > 1 && (
            <RadioGroup
              options={tokensOptions}
              value={selectedCardId}
              onValueChange={onChangeSelected}
              withDividers={false}
              className='mt-[8px]'
              itemClassName='items-center px-0 pe-[10px]'
              indicatorClassName='mt-0'
            />
          )}

          <Button
            onClick={onPay}
            disabled={selectedCardId === '' || purchasing}
            className='w-full h-auto mt-[12px] py-[10px]'
          >
            {purchasing ? <Spinner className='size-[20px] text-bg-day' /> : t('card_com_pay_button')}
          </Button>

          {showSubscriptionTerms && (
            <p className='mt-[12px] text-[12px] text-text-disabled-day! text-start'>
              {t.rich('card_com_subscription_terms', {
                terms: (chunks) => (
                  <Link
                    href='/agreement'
                    target={isTGMiniApp ? '_self' : '_blank'}
                    rel={isTGMiniApp ? '' : 'noopener noreferrer'}
                    className='underline'
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
